import { faEllipsisVertical, faMagnifyingGlass, faRotate, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { type SavedSearch } from 'bundles/connect/types';
import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Badge, Card, Dropdown, Spinner } from 'react-bootstrap-5';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/bs5/unstyled_dropdown_toggle';
import {
  checkSavedSearchForNewProperties, fetchExclusiveSearchCount, viewSavedSearchPropertiesUrl
} from 'bundles/connect/helpers/prospect_api';
import { faBellSlash, faLightbulbOn, faPenLine, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { formatShortDate } from '../../helpers/date_helpers';
import { displayToast } from 'bundles/connect/helpers/display_toast';

type Props = {
  savedSearch: SavedSearch;
  onDelete: (savedSearch: SavedSearch) => void;
  onEdit: (savedSearch: SavedSearch) => void;
  onUpdate: (savedSearch: SavedSearch) => void;
}

export default function SavedSearchCard({ savedSearch, onDelete, onEdit, onUpdate }: Props) {
  const exclusiveListingSearch = !savedSearch.offMarketSearch;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  thirtyDaysAgo.setHours(0, 0, 0, 0);

  const createdAtDate = new Date(savedSearch.createdAt);
  createdAtDate.setHours(0, 0, 0, 0);

  const newPropertiesCount = savedSearch.insightsPropertyIds
    ? Object.values(savedSearch.insightsPropertyIds).filter((idHash) => {
      const addedAtDate = new Date(idHash['addedAt']);
      addedAtDate.setHours(0, 0, 0, 0);
      return (
        addedAtDate >= thirtyDaysAgo &&
        addedAtDate > createdAtDate
      );
    }).length
    : 0;

  const [loading, setLoading] = useState(true);
  const [checkingForUpdates, setCheckingForUpdates] = useState(false);
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!exclusiveListingSearch) return;

    fetchExclusiveSearchCount(savedSearch.id).then((res) => {
      setCount(res);
      setLoading(false);
    });
  }, []);

  const checkForUpdates = () => {
    setCheckingForUpdates(true);
    checkSavedSearchForNewProperties(savedSearch.id).then((res: SavedSearch) => {
      if (Object.keys(res.insightsPropertyIds || {}).length === Object.keys(savedSearch.insightsPropertyIds || {}).length) {
        displayToast('No new properties found', 'info');
      }
      onUpdate(res);
    }).finally(() => {
      setCheckingForUpdates(false);
    });
  };

  const badgeLabel = (label: string) => {
    const items = label.split(',');
    if (items.length > 3) {
      return `${items[0]}, ${items[1]}, ${items[2]}, ${items.length - 3} more`;
    } else {
      return label;
    }
  };

  return (
    <Card className="h-100 shadow">
      <div className="d-flex align-items-center justify-content-between p-3" style={{ minHeight: 85 }}>
        <div className="d-flex flex-column gap-1" style={{ minWidth: 0 }}>
          <div className="text-truncate">
            <h5 className="fw-semibold fs-large mb-0">{savedSearch.name}</h5>
          </div>
          <div className="d-flex gap-1">
            {savedSearch.filterSummary.slice(0, 2).map((attr, idx) => (
              <Badge
                bg=""
                className="px-1 py-0 d-flex align-items-center fs-x-small border badge-muted badge-secondary"
                key={idx}
                style={{ minWidth: 0 }}
              >
                <span className="text-truncate">
                  {badgeLabel(attr.label)}
                </span>
              </Badge>
            ))}

            {savedSearch.filterSummary.length > 2 && (
              <Badge
                bg=""
                className="px-1 py-0 d-flex align-items-center fs-x-small badge-muted badge-secondary border"
              >
                {savedSearch.filterSummary.length - 2} more
              </Badge>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {savedSearch.offMarketSearch && !savedSearch.insightsPropertyIds && (
            <OverlayTrigger
              overlay={
                <Tooltip id="bell-slash-tooltip">
                  No notifications because search is too broad.
                </Tooltip>
              }
              placement="top"
            >
              <span>
                <FontAwesomeIcon icon={faBellSlash} />
              </span>
            </OverlayTrigger>
          )}
          <Dropdown>
            <Dropdown.Toggle as={UnstyledDropdownToggle} className="text-muted" id="dropdown-menu">
              <FontAwesomeIcon className="clickable px-2" icon={faEllipsisVertical} size="lg" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="py-0">
              <Dropdown.Item className="border-0 rounded-top" onClick={() => onEdit(savedSearch)}>
                <FontAwesomeIcon icon={faPenLine} />
                Rename
              </Dropdown.Item>
              <Dropdown.Divider className="my-0" />
              {!exclusiveListingSearch && savedSearch.lastCheckedForNewProperties && (
                <>
                  <Dropdown.Item
                    className="border-0"
                    disabled={checkingForUpdates}
                    onClick={() => checkForUpdates()}
                  >
                    <FontAwesomeIcon icon={faRotate} />
                    Check for Updates
                  </Dropdown.Item>
                  <Dropdown.Divider className="my-0" />
                </>
              )}
              <Dropdown.Item
                className="text-danger border-0 rounded-bottom"
                onClick={() => onDelete(savedSearch)}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="d-flex p-2 justify-content-between border-top">
        <div className="d-flex flex-column">
          {savedSearch.insightsPropertyIds && (
            <>
              <div className="d-flex align-items-center gap-1">
                {checkingForUpdates ? (
                  <Spinner animation="border" className="text-primary" size="sm" />
                ) : (
                  Object.keys(savedSearch.insightsPropertyIds).length.toLocaleString()
                )}
                {newPropertiesCount > 0 && (
                  <Badge
                    bg=""
                    className="px-1 py-0 d-flex align-items-center fs-x-small badge-muted badge-secondary border"
                  >
                    <FontAwesomeIcon className="text-primary" icon={faLightbulbOn} />
                    {newPropertiesCount.toLocaleString()} New
                  </Badge>
                )}
              </div>
              <div className="fs-x-small text-muted">
                Properties / Last Checked: {formatShortDate(savedSearch.lastCheckedForNewProperties)}
              </div>
            </>
          )}
        </div>
        {exclusiveListingSearch ? (
          <Button
            href={viewSavedSearchPropertiesUrl(savedSearch.id)}
            icon={faStar}
            variant="ghost"
          >
            View {loading ? <Spinner animation="border" size="sm" /> : count} matches
          </Button>
        ) : (
          <Button
            href={viewSavedSearchPropertiesUrl(savedSearch.id)}
            icon={faMagnifyingGlass}
            label="View Matches"
            variant="ghost"
          />
        )}
      </div>
    </Card>
  );
}
